// utils/countryUtils.js
import countries from "i18n-iso-countries";
import nlLocale from "i18n-iso-countries/langs/nl.json";
import enLocale from 'i18n-iso-countries/langs/en.json';

// Register locale once
countries.registerLocale(nlLocale);
countries.registerLocale(enLocale);

/**
 * Convert country code to country name in Dutch
 * @param {string} countryCode - ISO Alpha-2 country code (e.g., 'NL', 'DE', 'US')
 * @param {string} locale - Language locale (default: 'nl')
 * @returns {string} Country name in specified locale or the original code if not found
 */
export const getCountryName = (countryCode, locale = 'nl') => {
  if (!countryCode) return '';
  
  const countryName = countries.getName(countryCode, locale, { select: "alias" });
  return countryName || countryCode;
};

/**
 * Convert country name to country code
 * @param {string} countryName - Country name in specified locale
 * @param {string} locale - Language locale (default: 'nl')
 * @returns {string} ISO Alpha-2 country code or empty string if not found
 */
export const getCountryCode = (countryName, locale = 'nl') => {
  if (!countryName) return '';
  
  return countries.getAlpha2Code(countryName, locale) || '';
};

/**
 * Get all countries as array of objects with code and name
 * @param {string} locale - Language locale (default: 'nl')
 * @returns {Array} Array of objects with { code, name } properties
 */
export const getAllCountries = (locale = 'nl') => {
  const countryObj = countries.getNames(locale, { select: "alias" });
  
  return Object.entries(countryObj).map(([code, name]) => ({
    code,
    name,
  }));
};

/**
 * Check if a country code is valid
 * @param {string} countryCode - ISO Alpha-2 country code
 * @returns {boolean} True if valid country code
 */
export const isValidCountryCode = (countryCode) => {
  if (!countryCode) return false;
  return countries.isValid(countryCode);
};

// Language mapping configuration for supported countries
export const SUPPORTED_LANGUAGE_OPTIONS = [
  { code: "NL", lang: "Nederlands" },
  { code: "EN", lang: "English" },
  { code: "DE", lang: "Deutsch" },
  { code: "FR", lang: "Français" },
];

/**
 * Get the primary language for a given country code
 * @param {string} countryCode - ISO Alpha-2 country code (e.g., 'NL', 'EN', 'DE', 'FR')
 * @returns {string|undefined} Language name in native spelling or undefined if not found
 * 
 * @example
 * getLanguageFromCountryCode('NL') // Returns 'Nederlands'
 * getLanguageFromCountryCode('US') // Returns undefined
 */
export const getLanguageFromCountryCode = (countryCode) => {
  // Return early if no country code provided
  if (!countryCode) return undefined;
  
  // Normalize input to uppercase for case-insensitive matching
  const normalizedCode = countryCode.toUpperCase();
  
  // Find the language option that matches the country code
  const languageOption = SUPPORTED_LANGUAGE_OPTIONS.find(
    option => option.code === normalizedCode
  );
  
  // Return the language if found, undefined otherwise
  return languageOption?.lang;
};